import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import * as L from "leaflet";
import "leaflet-routing-machine";
import { osService } from "src/app/_services/osService.service";
import { Position, SearchPosition } from "src/app/helpers/types";

@Component({
  selector: "app-osmap",
  templateUrl: "./osmap.component.html",
  styleUrls: ["./osmap.component.scss"],
  standalone: false,
})
export class OsmapComponent implements OnInit, AfterViewInit {
  @Input() currentLocation?: { latitude: number; longitude: number };
  @Output() dragStart: EventEmitter<void> = new EventEmitter<void>();
  @Output() dragEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() draging: EventEmitter<void> = new EventEmitter<void>();
  @Output() openSaveModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() appLayerKey: any;
  @Input() selectedMapLayerConfig: any;
  @Input() modeSatellite :boolean = false;

  map!: L.Map;
  marker!: L.Marker;
  userInfoWindow: L.Popup | null = null;
  currentInfoWindow: L.Popup | null = null;
  headerText: string = this.translate.instant("PROVIDER.MAP.DEPLACER_ICON");
  mapTouched: boolean = false;
  searchPosition?: SearchPosition | null | undefined;

  startPosition: L.LatLng = new L.LatLng(35.83766268383463, 10.622713348112542); // Default value
  originPosition: { latitude: number; longitude: number } = {
    latitude: 35.83766268383463,
    longitude: 10.622713348112542,
  };
  destinationPosition: { latitude: number; longitude: number } = {
    latitude: 37.83766268,
    longitude: 10.6227133,
  };
  markersList: Position[] = [];
  constructor(
    private osService: osService,
    private translate: TranslateService,
    private el: ElementRef,
    private renderer: Renderer2
  ) {}
  routingControl: L.Routing.Control | null = null;

  ngAfterViewInit(): void {
    this.initializeMap();
    setTimeout(() => {
      this.map.invalidateSize();
    }, 100);
  }

  ngOnInit() {
    this.getSearchLocation();
  }
  ionViewDidEnter() {}
  initializeMap() {
    this.initMap();
    this.preventGestureRotation();
    this.initUserMarker();
    this.createMarkers();
    this.dragMarker();
  }

  // Leaflet n'a pas de rotation, mais sur WebKit (iOS) le geste pincer/pivoter
  // à deux doigts déclenche les évènements `gesture*` qui font tourner le
  // contenu de la page. On les neutralise pour garder la carte fixe.
  private preventGestureRotation() {
    const container = this.map?.getContainer();
    if (!container) {
      return;
    }
    const prevent = (event: Event) => event.preventDefault();
    container.addEventListener("gesturestart", prevent);
    container.addEventListener("gesturechange", prevent);
    container.addEventListener("gestureend", prevent);
  }
  initMap() {
    this.startPosition = this.currentLocation
      ? L.latLng(this.currentLocation.latitude, this.currentLocation.longitude)
      : L.latLng(35.83766268383463, 10.622713348112542);

    const mapOptions = {
      zoom: 15,
      center: this.startPosition,
      zoomControl: false,
    };

    this.map = L.map("mapId", mapOptions);
    let tileLayerUrl: string;
    let attribution: string;
    if (this.selectedMapLayerConfig === "MAPBOX" && this.appLayerKey) {
      const streetLayer = L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${this.appLayerKey}`,
        {
          maxZoom: 19,
          tileSize: 1024,
          zoomOffset: -2,
          attribution: "© Mapbox © OpenStreetMap contributors",
        }
      );

      const satelliteLayer = L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=${this.appLayerKey}`,
        {
          maxZoom: 19,
          tileSize: 1024,
          zoomOffset: -2,
          attribution: "© Mapbox © OpenStreetMap contributors",
        }
      );

      if (this.modeSatellite) {
        satelliteLayer.addTo(this.map);
      } else {
        streetLayer.addTo(this.map);
      }

      // L.control
      //   .layers({
      //     "Vue street": streetLayer,
      //     "Vue satellite ": satelliteLayer,
      //   })
      //   .addTo(this.map);
    } else {
      tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
      attribution =
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
      L.tileLayer(tileLayerUrl, {
        maxZoom: 19,
        attribution: attribution,
      }).addTo(this.map);
    }
  }

  initUserMarker() {
    this.marker = L.marker(this.startPosition, {
      draggable: false,
      icon: L.icon({
        iconUrl: "../../../assets/icon/mapLocationIcon.svg",
        iconSize: [40, 40], // Adjust the size of the icon as needed
      }),
    }).addTo(this.map);
  }

  dragMarker() {
    this.map.on("drag", () => {
      this.draging.emit();
      const newCenter = this.map.getCenter();
      if (this.marker) {
        this.marker.setLatLng(newCenter);
        const newPosition = {
          lat: newCenter.lat,
          lng: newCenter.lng,
        };
        localStorage.setItem("markerPosition", JSON.stringify(newPosition));
        const markerPosition2: Position = {
          label: "",
          coords: {
            latitude: newPosition.lat,
            longitude: newPosition.lng,
          },
        };
        this.osService.setMarkerLocation(markerPosition2);
      }
    });

    this.map.on("dragend", () => {
      const newCenter = this.map.getCenter();
      const newPosition = {
        lat: newCenter.lat,
        lng: newCenter.lng,
      };
      this.handleDragStart();
      const markerPosition2: Position = {
        label: "",
        coords: {
          latitude: newPosition.lat,
          longitude: newPosition.lng,
        },
      };
      this.osService.setMarkerLocation(markerPosition2);

      this.dragEnd.emit();
    });

    this.map.on("dragstart", () => {
      this.dragStart.emit();
    });
  }

  createMarkers() {
    for (const markerData of this.markersList) {
      const markerPosition = L.latLng(
        markerData.coords.latitude,
        markerData.coords.longitude
      );

      const marker = L.marker(markerPosition, {
        title: markerData.label,
      }).addTo(this.map);

      marker.on("click", () => {
        if (this.currentInfoWindow) {
          this.currentInfoWindow.remove();
        }

        const popup = L.popup()
          .setLatLng(markerPosition)
          .setContent(markerData.label)
          .openOn(this.map);

        this.currentInfoWindow = popup;
        this.setBounce(marker);
      });
    }
  }

  getSearchLocation() {
    this.osService.SearchLocationSubject$.subscribe((searchPosition) => {
      this.searchPosition = searchPosition;
      const position = searchPosition?.position![0];
      if (searchPosition?.title) {
        this.currentLocation = {
          latitude: position!.lat,
          longitude: position!.long,
        };
        if (this.marker) {
          const newPosition = L.latLng(
            this.currentLocation.latitude,
            this.currentLocation.longitude
          );

          this.marker.setLatLng(newPosition);
          this.moveMapToLocation(
            this.currentLocation.latitude,
            this.currentLocation.longitude,
            15
          );
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ("currentLocation" in changes) {
      if (this.currentLocation) {
        if (this.marker) {
          const newPosition = L.latLng(
            this.currentLocation.latitude,
            this.currentLocation.longitude
          );

          this.marker.setLatLng(newPosition);
          this.moveMapToLocation(
            this.currentLocation.latitude,
            this.currentLocation.longitude,
            15
          );
        }
      }
    }
  }

  moveMapToLocation(lat: number, lng: number, zoom: number) {
    if (this.map) {
      this.map.setView([lat, lng], zoom);
    }
  }
  setBounce(marker: any) {
    marker.setBounce();
    setTimeout(
      (() => {
        marker.setAnimation(null);
      }).bind(marker),
      1400
    );
  }

  handleDragStart() {
    this.mapTouched = true;
    this.headerText = this.translate.instant("PROVIDER.MAP.PLACEMENT_ERROR");
  }
  calculateRoute() {
    if (this.destinationPosition && this.originPosition) {
      const request = {
        origin: this.originPosition,
        destination: this.destinationPosition,
        travelMode: "DRIVING",
      };
      this.traceRoute(request);
    }
  }

  traceRoute(request: any) {
    if (this.routingControl) {
      this.map.removeControl(this.routingControl);
    }

    this.routingControl = L.Routing.control({
      waypoints: [
        L.latLng(request.origin.latitude, request.origin.longitude),
        L.latLng(request.destination.latitude, request.destination.longitude),
      ],
      routeWhileDragging: true,
      show: false, // Hide the route description pane
    }).addTo(this.map);
  }
  hideRoutingContainer(): void {
    const routingContainer = this.el.nativeElement.querySelector(
      ".leaflet-routing-container"
    );

    if (routingContainer) {
      this.renderer.setStyle(routingContainer, "display", "none");
    } else {
      console.error("Routing container not found");
    }
  }
}
